import { useQuery } from '@apollo/client/react';
import { useEffect, useRef } from 'react';
import { useToast } from '@insio/ui-toolkit';
import { GetProfileDocument } from '@/graphql';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { quotaExcessCount, toQuotaInfo } from '@/features/admin/utils/quota-utils';

const STORAGE_KEY_PREFIX = 'overage-dismissed-';

/**
 * Shows a one-time warning toast when the account exceeds its monthly extraction
 * limit on an overage-enabled plan. Dismissed state persists per billing period
 * in localStorage so the toast appears at most once per billing cycle.
 */
export function useOverageNotification() {
  const { data } = useQuery(GetProfileDocument, { fetchPolicy: 'cache-first' });
  const { show } = useToast();
  const t = useFeatureTranslation('systemSettings.payments');
  const shownRef = useRef(false);

  // A managed client sees nothing about overage — that is its master account's concern (plan §6.4).
  const quota = toQuotaInfo(data?.profile?.account?.extractionQuota);

  useEffect(() => {
    if (!quota || shownRef.current) return;

    const excess = quotaExcessCount(quota);
    if (!quota.canExceedLimit || excess <= 0) return;

    const storageKey = `${STORAGE_KEY_PREFIX}${quota.period}`;
    if (localStorage.getItem(storageKey)) return;

    shownRef.current = true;
    localStorage.setItem(storageKey, '1');

    const remaining = quota.hardCap != null ? Math.max(quota.hardCap - quota.used, 0) : null;
    const messageKey =
      remaining != null
        ? 'quota.overageNotification.messageWithRemaining'
        : 'quota.overageNotification.message';

    show({
      type: 'warning',
      title: t('quota.overageNotification.title'),
      message: t(messageKey, { count: excess, remaining: remaining ?? 0 }),
      duration: 10000
    });
  }, [quota, show, t]);
}
